// src/billing/services/print-epoch.ts
import { useAuthStore } from '@/auth/auth-store';
import { usePrintPreviewStore } from '@/billing/store/print-preview-store';

/**
 * Monotonic "print epoch" — the single source of truth for whether an
 * in-flight invoice/receipt print request (`usePrintInvoice` /
 * `usePrintReceipt`) is still allowed to write its result into the shared,
 * module-global `usePrintPreviewStore`.
 *
 * Replaces the earlier per-request `userId` guard (PROD-1001 Codex v4 P1),
 * which had two gaps found in Codex v5 review:
 *
 *   P1 (superseded concurrent prints) — clicking Print on two rows before
 *   either resolves ran both mutations; whichever resolved LAST called
 *   `store.open()`, so an earlier click's (possibly different patient's)
 *   document could be replaced/auto-printed over by a stale, later-arriving
 *   response. `userId` alone can't detect this — both requests are the SAME
 *   user. Fix: bump the epoch at the START of every print request too, so
 *   starting a newer print immediately invalidates any older one still in
 *   flight.
 *
 *   P2 (same-user re-auth not detected) — the old guard passed whenever the
 *   resolving `userId` matched the requesting `userId`, including "logged
 *   out and back in as the same person" — a stale pre-logout document could
 *   still open. Fix: the identity subscription below bumps the epoch on
 *   EVERY signed-in-identity transition (`user.userId` changes to/from any
 *   value, including null), not just "different user" — logout then login
 *   as the same person is two transitions, both bump.
 *
 * Bumped on:
 *   - the start of every `usePrintInvoice` / `usePrintReceipt` request
 *     (supersedes older in-flight prints),
 *   - login and logout (via the identity subscription below),
 *   - a tenant-scope change WITHOUT a new login — e.g. `setActivePractice`
 *     (the calendar PracticeSwitcher) updates `user.practiceId` in place,
 *     same `userId`, no logout/login transition (Codex v6 P1). A print
 *     resolving after that switch must not open practice-A's document in
 *     practice-B context, so the identity subscription below watches the
 *     FULL tenant scope, not just `userId`,
 *   - `AppShell` unmount (see `app-shell.tsx`) — a safety net independent of
 *     auth state, in case the shell unmounts for a reason other than logout.
 *
 * ALSO closes any already-open preview on a tenant-identity transition (Codex
 * v7 P1): the epoch alone only invalidates an IN-FLIGHT print request — it
 * does nothing for a preview that already resolved and is currently mounted.
 * A practice switch does not unmount `AppShell`, so without this a practice-A
 * invoice/receipt PDF (and its live blob URL) could keep showing on screen
 * after the app switches to practice B. The SAME subscription that bumps the
 * epoch on an identity change also tears down the store (`close()`, which
 * revokes any store-owned blob per `print-preview-store.ts`), guarded to a
 * no-op when nothing is open so a routine identity re-render (or a switch
 * with no preview showing) doesn't churn the store.
 *
 * NOT bumped/closed by token refresh (`forceRefresh`): that only replaces
 * `accessToken` / `refreshToken` on the auth store, never `user`, so the
 * identity subscription does not fire for it — the same reason the old
 * guard anchored on `user.userId` rather than `accessToken` in the first
 * place (an access-token rotation mid-request must not false-positive this
 * check).
 */
let printEpoch = 0;

/** Invalidate any in-flight print request that started before this call. */
export function bumpPrintEpoch(): void {
  printEpoch += 1;
}

/** The epoch a resolving print request must still match to be displayed. */
export function currentPrintEpoch(): number {
  return printEpoch;
}

/**
 * The full tenant-scoped identity the epoch subscription watches. `userId`
 * alone misses a practice switch (`setActivePractice`) — same user, new
 * `practiceId`, same as far as a userId-only guard is concerned. Composite of
 * every scope field a resolving print result could be wrongly shown against;
 * ANY change here (including logout → null) bumps the epoch. `organizationId`
 * is included for completeness (tenant/org switch), even though today's
 * `setActivePractice` only mutates `practiceId`.
 */
function identityOf(state: {
  user: { userId: string; practiceId?: string; organizationId?: string } | null;
}): string | null {
  const { user } = state;
  if (!user) return null;
  return `${user.userId}::${user.practiceId ?? ''}::${user.organizationId ?? ''}`;
}

let lastIdentity = identityOf(useAuthStore.getState());
useAuthStore.subscribe((state) => {
  const identity = identityOf(state);
  if (identity !== lastIdentity) {
    lastIdentity = identity;
    bumpPrintEpoch();
    // Tear down any preview left open from the prior tenant scope (Codex v7
    // P1). `close()` revokes the owned blob itself; only call it when a
    // preview is actually showing so a plain identity re-render (or a switch
    // with nothing open) is a no-op.
    if (usePrintPreviewStore.getState().url !== null) {
      usePrintPreviewStore.getState().close();
    }
  }
});
